/**
 * /pressure-testing/instruments — the instrument register (fieldiq-api#907
 * `GET /pressure-testing/instruments`).
 *
 * STATUS IS THE API'S. `calibration_status` is computed server-side against the
 * tenant's day and warn window; this screen colours it and never derives it.
 *
 * FILTERING IS THE SERVER'S. `status`, `kind` and `q` are query params the API
 * reads; the list it returns is the answer.
 *
 * THE COUNTS ARE A TALLY OF THE UNFILTERED LIST, and that is honest here only
 * because (a) no PT endpoint returns a count, and (b) the list is unpaginated —
 * the API answers every live instrument. A second, unfiltered read feeds the
 * stats and chips, so a chip keeps its count while another chip is selected.
 * Counting `calibration_status` values the API returned is not recomputing
 * status. If the list is ever paginated, these must come from the API.
 *
 * WRITES: Add instrument, for PT_ADMIN_ROLES (admin, office_staff).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Clock, Gauge, Loader2, Plus, Search, HelpCircle } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtInstruments } from '../../hooks/usePressureTesting';
import { formatCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminPt, formatPressureRange, PT_KIND_LABEL, ptKindLabel, ptStatusSkin } from '../../lib/pressure-testing';
import {
  PT_CALIBRATION_STATUSES,
  PT_INSTRUMENT_KINDS,
  type PtInstrument,
  type PtPressureUnit,
} from '../../types/pressure-testing';
import { AddInstrumentDialog } from './PtDialogs';
import { PtChip, PtEmpty, PtPanel, PtStatusBadge } from './PtBits';

const isStatus = (v: string | null): v is (typeof PT_CALIBRATION_STATUSES)[number] =>
  !!v && (PT_CALIBRATION_STATUSES as readonly string[]).includes(v);
const isKind = (v: string | null): v is (typeof PT_INSTRUMENT_KINDS)[number] =>
  !!v && (PT_INSTRUMENT_KINDS as readonly string[]).includes(v);

const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

/** `12 days`, `3 days overdue`, `Due today`. From the API's days_until_due; null → nothing. */
export function dueHint(days: number | null | undefined): string | null {
  if (typeof days !== 'number' || !Number.isFinite(days)) return null;
  if (days === 0) return 'Due today';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} overdue`;
  return `In ${days} day${days === 1 ? '' : 's'}`;
}

/** A tally of one status over the API's unfiltered list — see the docblock. Undefined until loaded. */
function tally(rows: PtInstrument[] | undefined, pick: (i: PtInstrument) => boolean): number | undefined {
  return rows ? rows.filter(pick).length : undefined;
}

export default function InstrumentsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const kindParam = searchParams.get('kind');
  const status = isStatus(statusParam) ? statusParam : '';
  const kind = isKind(kindParam) ? kindParam : '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => ({ ...(status && { status }), ...(kind && { kind }), ...(q && { q }) }),
    [status, kind, q],
  );
  const filtered = !!(status || kind || q);
  const listQuery = usePtInstruments(params);
  // The unfiltered register, for the counts. The same query as `listQuery`
  // when nothing is filtered, so React Query serves both from one request.
  const allQuery = usePtInstruments();

  const instruments = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta ?? allQuery.data?.meta;
  const unit: PtPressureUnit | null = meta?.pressure_unit ?? null;
  const listFailure = queryLoadFailure(listQuery);
  // A count is shown only when the unfiltered read arrived. A failed or
  // loading read is no number, never a zero.
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countsLoading = allQuery.isLoading;
  // The Add dialog labels its range inputs with the unit, so it waits for it.
  const canAdd = canAdminPt(user?.role) && !!unit;

  const setParam = (key: 'status' | 'kind', next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set(key, next);
    else nextParams.delete(key);
    setSearchParams(nextParams, { replace: true });
  };

  const open = (id: string) => navigate(`/pressure-testing/instruments/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  const countOf = (pick: (i: PtInstrument) => boolean) => displayValue(tally(all, pick));

  return (
    <div data-testid="pt-instruments-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Instruments"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add instrument
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <Gauge className="size-4" aria-hidden="true" />,
            label: 'Total',
            sub: 'Registered',
            tone: 'neutral',
            loading: countsLoading,
            value: countOf(() => true),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Due soon',
            sub: 'Warn window',
            tone: 'accent',
            loading: countsLoading,
            value: countOf((i) => i.calibration_status === 'due_soon'),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Overdue',
            sub: 'Past due',
            tone: 'accent',
            loading: countsLoading,
            value: countOf((i) => i.calibration_status === 'overdue'),
          },
          {
            icon: <HelpCircle className="size-4" aria-hidden="true" />,
            label: 'Never',
            sub: 'No record',
            tone: 'neutral',
            loading: countsLoading,
            value: countOf((i) => i.calibration_status === 'never'),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by calibration status">
            <PtChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {PT_CALIBRATION_STATUSES.map((s) => (
              <PtChip
                key={s}
                label={ptStatusSkin(s).label}
                active={status === s}
                onClick={() => setParam('status', s)}
                count={countsLoading ? undefined : countOf((i) => i.calibration_status === s)}
              />
            ))}
          </div>
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by kind">
            <PtChip label="All kinds" active={!kind} onClick={() => setParam('kind', '')} />
            {PT_INSTRUMENT_KINDS.map((k) => (
              <PtChip
                key={k}
                label={PT_KIND_LABEL[k]}
                active={kind === k}
                onClick={() => setParam('kind', k)}
                count={countsLoading ? undefined : countOf((i) => i.specs?.instrument_kind === k)}
              />
            ))}
          </div>

          <label className="relative block">
            <span className="sr-only">Search name, serial, model or manufacturer</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, serial, model or manufacturer"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <PtPanel
            eyebrow="Calibration register"
            title="Instruments"
            testId="pt-instrument-register"
            footer={
              !listQuery.isLoading && !listFailure && instruments.length > 0
                ? filtered
                  ? `Showing ${instruments.length} of ${countOf(() => true)} instruments`
                  : `${instruments.length} instruments`
                : undefined
            }
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading instruments" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="instruments" />
            ) : instruments.length === 0 ? (
              filtered ? (
                <PtEmpty>No instrument matches these filters.</PtEmpty>
              ) : (
                <div className="px-4 py-10 text-center" data-testid="pt-instruments-empty">
                  <p className="text-sm text-fieldiq-text">There are no instruments in the register.</p>
                  {canAdd && (
                    <button
                      type="button"
                      className="fi-action fi-action--standard fi-action--primary mt-4"
                      onClick={() => setAdding(true)}
                    >
                      <Plus className="size-4" aria-hidden="true" />
                      Add instrument
                    </button>
                  )}
                </div>
              )
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Name / serial</th>
                        <th scope="col" className="px-4 py-3">Kind</th>
                        <th scope="col" className="px-4 py-3 text-right">Range</th>
                        <th scope="col" className="px-4 py-3">Last cal</th>
                        <th scope="col" className="px-4 py-3">Due</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {instruments.map((i) => (
                        <tr
                          key={i.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(i.id)}
                          onKeyDown={rowKeys(i.id)}
                          data-testid="pt-instrument-row"
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${
                            ptStatusSkin(i.calibration_status).rail
                          }`}
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-extrabold text-white">{i.name || '—'}</p>
                            <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{i.serial_number || '—'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{ptKindLabel(i.specs?.instrument_kind)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                            {formatPressureRange(i.specs?.range_min, i.specs?.range_max, unit)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(i.latest_calibration?.calibrated_on)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                            {day(i.latest_calibration?.due_on)}
                            {dueHint(i.days_until_due) && (
                              <p className="mt-0.5 text-xs text-fieldiq-muted">{dueHint(i.days_until_due)}</p>
                            )}
                          </td>
                          <td className="px-4 py-3"><PtStatusBadge status={i.calibration_status} /></td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {instruments.map((i) => (
                    <div
                      key={i.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(i.id)}
                      onKeyDown={rowKeys(i.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-extrabold text-white">{i.name || '—'}</p>
                          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{i.serial_number || '—'}</p>
                        </div>
                        <PtStatusBadge status={i.calibration_status} />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                        <span>{ptKindLabel(i.specs?.instrument_kind)}</span>
                        <span>{formatPressureRange(i.specs?.range_min, i.specs?.range_max, unit)}</span>
                        <span>Due {day(i.latest_calibration?.due_on)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </PtPanel>
        </div>
      </section>

      {canAdminPt(user?.role) && <AddInstrumentDialog open={adding} onClose={() => setAdding(false)} unit={unit} />}
    </div>
  );
}
